export interface DictionaryItem {
  id: number;
  code: string;
  name: string;
}

export interface Skill {
  id: number;
  name: string;
}

export interface CareerFilters {
  location: string[];
  type: string[];
  skills: string[];
}

export interface Job {
  id: number;
  title: string;
  location?: DictionaryItem;
  type?: DictionaryItem;
  description?: string;
  long_description?: string;
  responsibilities?: string;
  requirements?: string;
  nice_to_have?: string;
  what_we_offer?: string;
  skills?: Skill[];
}

export interface JobsPagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface FetchJobsResult {
  jobs: Job[];
  pagination: JobsPagination;
}

const API_URL =
  (process.env.STRAPI_BACKEND_URL || 'https://strapi.bekoretech.com') + '/api';

function normalizeJob(raw: any): Job {
  return {
    id: raw.id,
    title: raw.title,
    location: raw.location
      ? {
          id: raw.location.id,
          code: raw.location.code,
          name: raw.location.name,
        }
      : undefined,
    type: raw.type
      ? {
          id: raw.type.id,
          code: raw.type.code,
          name: raw.type.name,
        }
      : undefined,
    description: raw.description,
    long_description: raw.long_description,
    responsibilities: raw.responsibilities,
    requirements: raw.requirements,
    nice_to_have: raw.nice_to_have,
    what_we_offer: raw.what_we_offer,
    skills: Array.isArray(raw.skills)
      ? raw.skills.map((s: any) => ({
          id: s.id,
          name: s.name,
        }))
      : [],
  };
}

export async function fetchJobs({
  page,
  pageSize,
  filters,
}: {
  page: number;
  pageSize: number;
  filters: CareerFilters;
}): Promise<FetchJobsResult> {
  const params = new URLSearchParams();

  params.append('pagination[page]', String(page));
  params.append('pagination[pageSize]', String(pageSize));

  params.append('populate[location]', 'true');
  params.append('populate[type]', 'true');
  params.append('populate[skills]', 'true');

  filters.location.forEach((value, i) => {
    params.append(`filters[location][code][$in][${i}]`, value);
  });

  filters.type.forEach((value, i) => {
    params.append(`filters[type][code][$in][${i}]`, value);
  });

  filters.skills.forEach((value, i) => {
    params.append(`filters[skills][name][$in][${i}]`, value);
  });

  try {
    const res = await fetch(`${API_URL}/jobs?${params.toString()}`);
    const json = await res.json();

    if (!res.ok) {
      throw new Error(json?.error?.message || 'Failed to fetch jobs');
    }

    return {
      jobs: (json.data || []).map(normalizeJob),
      pagination: json?.meta?.pagination || {
        page: 1,
        pageSize: pageSize,
        pageCount: 1,
        total: 0,
      },
    };
  } catch (error) {
    return {
      jobs: [],
      pagination: {
        page: 1,
        pageSize,
        pageCount: 1,
        total: 0,
      },
    };
  }
}

export async function fetchLocations(): Promise<DictionaryItem[]> {
  const res = await fetch(`${API_URL}/locations`);
  const json = await res.json();

  return (json.data || []).map((item: any) => ({
    id: item.id,
    code: item.code,
    name: item.name,
  }));
}

export async function fetchJobTypes(): Promise<DictionaryItem[]> {
  const res = await fetch(`${API_URL}/types`);
  const json = await res.json();

  return (json.data || []).map((item: any) => ({
    id: item.id,
    code: item.code,
    name: item.name,
  }));
}

export async function fetchSkills(): Promise<string[]> {
  const res = await fetch(`${API_URL}/skills`);
  const json = await res.json();

  return (json.data || []).map((item: any) => item.name);
}
